// ポータル（ダッシュボード）のブート。
//
// ⚠️ **Phaser を値として import しないこと**（`import type` のみ）。ゲーム本体は
//    [games/registry.ts](games/registry.ts) 経由で**動的 import** し、「遊ぶ」を押すまで
//    落とさない。ここに静的 import を1つ足すだけで、遊ばない人にも 1.2MB を払わせることになる。
import type Phaser from 'phaser';
import { GAMES, findGame, type GameRuntime } from '@/games/registry';
import { reconcileSettingsWithServer, holdBgm, releaseBgm } from '@/platform/net/settings';
import { isRegistered } from '@/platform/net/playerId';
import { renderDashboard } from '@/platform/portal/dashboard';
import { setGameTitle } from '@/platform/portal/documentTitle';
import { HOME_BUTTON_VISIBLE_CLASS, trackHomeButton } from '@/platform/portal/homeButton';
import { maybeShowSmallScreenNotice } from '@/platform/portal/smallScreenNotice';
import { loadLanguage, t, type CatalogName } from '@/platform/i18n';
import { getLanguage } from '@/platform/net/playerId';

// ---- DOM 参照 ----
const dashboardEl = document.getElementById('dashboard') as HTMLElement;
const gameEl = document.getElementById('game') as HTMLElement;
const homeBtn = document.getElementById('home-btn') as HTMLButtonElement;

// ---- Phaser ゲームは「遊ぶ」まで生成しない（ダッシュボードを軽く保つ） ----
let game: Phaser.Game | null = null;
const portraitMq = window.matchMedia('(orientation: portrait) and (pointer: coarse)');

/** 起動済みのゲームがどれか（別ゲームへ移るときは作り直す）。 */
let currentGameKey: string | null = null;

/** 「← ゲーム一覧へ」のキャンバス追従を止める関数（Phaser を作り直すときに呼ぶ）。 */
let untrackHomeBtn: (() => void) | null = null;

/**
 * ゲームループの休止/再開を状況に合わせて同期。BGM もループに追従させる
 * （止まっている間は無音・再開で続きから）。
 * - ダッシュボード表示中: 休止（タイムメーター等を止める）
 * - ゲーム中 かつ スマホ縦向き: 休止（回転案内オーバーレイ表示中）
 * - ゲーム中 かつ 横向き/PC: 稼働
 */
function syncLoop(): void {
  if (!game) return;
  const inGame = document.body.classList.contains('in-game');
  if (inGame && !portraitMq.matches) {
    game.loop.wake();
    remeasure(game);
    releaseBgm(game.sound, 'inactive');
  } else {
    game.loop.sleep();
    holdBgm(game.sound, 'inactive');
  }
}

/**
 * 画面サイズを測り直す（**縦向きから横向きへ戻したとき**）。
 *
 * 🔴 休止中に届いたリサイズは信用できない（2026-08-16 に本番 iPad で報告）。
 * 症状は「遊び方を開いたまま縦にして横へ戻すと**上端の見出しと戻るボタンが消え、
 * 画面から抜け出せない**」。iOS Safari は回転の**途中経過のサイズ**を報告することがあり、
 * その値で `Scale.FIT` を計算するとキャンバスが誤った寸法のまま固まる。
 * ⚠️ 縦向きの間は `loop.sleep()` で RAF を止めているので、**自力で測り直す機会が来ない**。
 *
 * ⇒ 起こした直後に1回、回転アニメーションが終わる頃にもう1回測り直す。
 *   `refresh()` は同じ寸法なら何もしないので、余分に呼んでも害はない。
 * ⚠️ Playwright の viewport 切り替えでは**再現しない**（一度に正しい寸法へ変わるため）。
 *   実機の回転でしか出ないので、直したことの確認も実機で行う。
 */
function remeasure(g: Phaser.Game): void {
  g.scale.refresh();
  window.setTimeout(() => g.scale.refresh(), 300);
}

/**
 * ゲームへ入る（初回は生成、以降は Title へ戻して再開）。
 *
 * **そのゲームの翻訳カタログを先に読み込む**（仕様2＝言語コード×ゲーム種別コードで管理）。
 * `t()` は同期なので、Phaser のシーンが `create()` に入る前に揃っている必要がある。
 * カタログ名は `game_key` と同じ値を使う（種別コードを別に持たない）。
 */
async function enterGame(gameKey: string): Promise<void> {
  const entry = findGame(gameKey);
  if (!entry) return; // 未知のキー（カードの data-play とレジストリの食い違い）。何もしない

  // 翻訳カタログと**ゲーム本体を並行で**落とす（どちらも初回だけ実際に通信する）。
  const [, runtime] = await Promise.all([
    loadLanguage(getLanguage(), [entry.key as CatalogName]),
    entry.load(),
  ]);

  document.body.classList.add('in-game');
  gameEl.style.display = 'flex';
  // タブに今遊んでいるゲームを出す（戻りは `renderDashboard` がポータル名へ戻す）。
  // ゲーム名は**ポータルのカタログ**にあるので、上の `loadLanguage` の完了を待つ必要はない。
  setGameTitle(entry.key);

  // 別のゲームへ移るときは Phaser ごと作り直す（シーン構成が違うため使い回せない）。
  if (game && currentGameKey !== entry.key) {
    untrackHomeBtn?.(); // 破棄するキャンバスを掴んだままにしない
    untrackHomeBtn = null;
    await destroyGame(game);
    game = null;
    currentGameKey = null;
  }
  if (!game) {
    game = startRuntime(runtime); // Boot→Preload→Title と進む
    currentGameKey = entry.key;
    // 「← ゲーム一覧へ」をキャンバスへ貼り付ける。**ゲーム側が左上を安全に使える**ように
    // 占有範囲を設計座標で固定する（理由は homeButton.ts の注記＝Chrome/Safari 差の正体）。
    // 🔴 出るのは**入口シーン（タイトル）だけ**。シーン名は runtime が名乗るものを渡す
    //   ＝ platform がゲームを名指ししない（HOME_BUTTON_VISIBLE_CLASS の注記）。
    untrackHomeBtn = trackHomeButton(homeBtn, game, runtime.ENTRY_SCENE_KEY);
  } else {
    // 同じゲームが起動済み: 実行中シーンを停止して最初のシーンへ戻す。
    // **シーン名は runtime が名乗る**（ここで `'Title'` を直書きすると、その名前の
    // シーンを持たないゲームで `Scene key not found` になる）。
    game.scene.getScenes(true).forEach((s) => game!.scene.stop(s.scene.key));
    game.scene.start(runtime.ENTRY_SCENE_KEY);
    syncLoop();
  }
}

function startRuntime(runtime: GameRuntime): Phaser.Game {
  return runtime.createGame({ parent: 'game', onReady: syncLoop });
}

/**
 * Phaser を**確実に**破棄する（別のゲームへ移るとき）。
 *
 * ⚠️ `Game.destroy()` は後始末を**予約するだけ**。実体（キャンバス除去・シーン破棄・
 * レンダラー破棄・ループ停止）は**次の `step()`** で走る（`pendingDestroy` → `runDestroy`）。
 * ところがダッシュボードへ戻るとき `syncLoop()` が `loop.sleep()` で RAF を止めているので、
 * 素直に呼ぶと **`step()` が二度と来ず、予約が永久に処理されない**。
 * 症状は「別のゲームを開くと前のゲームのキャンバスが残って2枚並ぶ」
 * （＋シーン・レンダラー・イベントも生きたまま残る）。**2本目を足して初めて到達する経路**。
 *
 * したがって **予約してから `wake()` で起こす**。`wake()` は最後に `tick()` を呼ぶので、
 * その場で `runDestroy` まで走り切る（`destroy` イベントも同期で飛ぶ）。
 * 万一イベントが来なくても先へ進めるよう保険のタイムアウトを置く（無言で固まるより良い）。
 */
function destroyGame(g: Phaser.Game): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      resolve();
    };
    // イベント名は**文字列リテラル**で書く。`Phaser.Core.Events.DESTROY` は値なので、
    // ここで参照すると初期バンドルに Phaser 本体が入ってしまう（このファイルは `import type` のみ）。
    g.events.once('destroy', finish);
    g.destroy(true);
    g.loop.wake();
    window.setTimeout(finish, 1000);
  });
}

/** ダッシュボードへ戻る（ゲームループ休止＋ランキング再取得）。 */
function exitToDashboard(): void {
  document.body.classList.remove('in-game');
  // ゲームは生かしたまま（同じゲームへ戻るのが速い）＝ poststep も止まらないが、
  // ループは syncLoop で休止するので**クラスは最後の値のまま固まる**。
  // 表示規則は `body.in-game` も要るので実害は無いが、DOM の状態が嘘になるので落としておく。
  homeBtn.classList.remove(HOME_BUTTON_VISIBLE_CLASS);
  gameEl.style.display = 'none';
  syncLoop(); // 休止
  showDashboard();
}

function showDashboard(): void {
  renderDashboard(dashboardEl, { onPlay: (key) => void enterGame(key) });
}

// ---- 配線 ----
homeBtn.addEventListener('click', exitToDashboard);
portraitMq.addEventListener('change', syncLoop);

/**
 * `index.html` に直接書かれた文言（ホームボタン・回転案内・小画面案内）を翻訳で置き換える。
 * これらは Phaser でも DOM ポータルでもない「外枠」なので、ここでまとめて面倒を見る。
 * HTML 側の中身は**カタログが読めなかったときのフォールバック**として残してある。
 */
function applyStaticI18n(): void {
  document.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    const key = el.dataset.i18n;
    if (key) el.textContent = t(key);
  });
}

/**
 * 起動。**ポータルの翻訳カタログを読んでから**描画する（未翻訳のちらつきを避ける）。
 * ゲーム側のカタログは `enterGame` が遊ぶ直前に読む。
 */
async function boot(): Promise<void> {
  await loadLanguage(getLanguage(), ['portal']);
  applyStaticI18n();
  showDashboard();
  // スマホ（小画面）は推奨環境外。初回だけ案内を出す（遊ぶこと自体は妨げない）。
  maybeShowSmallScreenNotice();

  // 登録プレイヤーなら起動時にサーバー設定・ステージ進捗を取り込む（キャッシュを早期に温める）。
  // 間に合わなくても各シーンで再取得されるので待たない。
  void reconcileSettingsWithServer(); // ゲストは内部で no-op
  // 進捗は**登録プレイヤーだけ**なので、ここで門を閉めてから登録簿を回す。
  // 各ゲームの進捗モジュールは動的 import なので、ゲストは1バイトも落とさない。
  if (isRegistered()) {
    for (const g of GAMES) void g.reconcileProgress?.();
  }
}

void boot();
